import { type KeyboardEvent } from 'react';

import { BlogPublicCategory } from '@/lib/entities/blog';

import { RightSmallIcon } from '@/lib/shared/icons/RightSmallIcon';
import { SearchIcon } from '@/lib/shared/icons/SearchIcon';

import { useTranslation } from 'next-i18next';
import Link from 'next/link';

import { BlogHubTags } from './BlogHubTags';

import styles from '../BlogHubPage.module.scss';

type BlogHubChromeProps = {
  locale: string;
  categories: BlogPublicCategory[];
  tags?: string[];
  title: string;
  subtitle?: string | null;
  breadcrumbCurrent?: string | null;
  activeCategorySlug?: string | null;
  searchDefault?: string;
  showChips?: boolean;
  activeTag?: string | null;
};

const handleChipKeyDown = (event: KeyboardEvent<HTMLAnchorElement>) => {
  if (event.key === ' ') {
    event.preventDefault();
    event.currentTarget.click();
  }
};

export const BlogHubChrome = ({
  locale,
  categories,
  tags = [],
  title,
  subtitle = null,
  breadcrumbCurrent = null,
  activeCategorySlug = null,
  searchDefault = '',
  showChips = true,
  activeTag = null,
}: BlogHubChromeProps) => {
  const { t } = useTranslation('index', { keyPrefix: 'blog' });
  const activeCategory = activeCategorySlug ?? 'all';

  return (
    <>
      <header className={styles.hub__header}>
        <div className={styles.hub__headerNav}>
          <nav className={styles.hub__breadcrumbs} aria-label="Breadcrumb">
            <ol className={styles.hub__breadcrumbsList}>
              <li className={styles.hub__breadcrumbsItem}>
                <Link href="/" className={styles.hub__breadcrumbsLink}>
                  {t('home')}
                </Link>
                <RightSmallIcon className={styles.hub__breadcrumbsChevron} />
              </li>
              <li className={styles.hub__breadcrumbsItem}>
                {breadcrumbCurrent ? (
                  <>
                    <Link href="/blog" className={styles.hub__breadcrumbsLink}>
                      {t('navTitle')}
                    </Link>
                    <RightSmallIcon
                      className={styles.hub__breadcrumbsChevron}
                    />
                  </>
                ) : (
                  <span>{t('navTitle')}</span>
                )}
              </li>
              {breadcrumbCurrent ? (
                <li className={styles.hub__breadcrumbsItem}>
                  <span className={styles.hub__breadcrumbsCurrent}>
                    {breadcrumbCurrent}
                  </span>
                </li>
              ) : null}
            </ol>
          </nav>
        </div>
        <div className={styles.hub__titleRow}>
          <h1 className={styles.hub__title}>{title}</h1>
          <form
            className={styles.hub__search}
            role="search"
            action={`/${locale}/blog/search`}
            method="get"
          >
            <input
              type="search"
              name="query"
              className={styles.hub__searchInput}
              defaultValue={searchDefault}
              placeholder={t('search')}
              aria-label={t('search')}
              autoComplete="off"
            />
            <button
              type="submit"
              className={styles.hub__searchSubmit}
              aria-label={t('search')}
            >
              <SearchIcon className={styles.hub__searchIcon} />
            </button>
          </form>
        </div>
        {subtitle ? <p className={styles.hub__subtitle}>{subtitle}</p> : null}
      </header>

      {showChips && categories.length > 0 ? (
        <nav className={styles.hub__filters} aria-label={t('categoriesLabel')}>
          {categories.map((category: BlogPublicCategory) => {
            const isActive = activeCategory === category.slug;
            return (
              <Link
                key={category.id}
                href={`/blog/${category.slug}`}
                className={isActive ? styles.hub__chipActive : styles.hub__chip}
                aria-current={isActive ? 'page' : undefined}
                tabIndex={0}
                onKeyDown={handleChipKeyDown}
              >
                {category.title}
              </Link>
            );
          })}
          <Link
            href="/blog"
            className={
              activeCategory === 'all'
                ? styles.hub__chipShowAllActive
                : styles.hub__chipShowAll
            }
            aria-current={activeCategory === 'all' ? 'page' : undefined}
            tabIndex={0}
            onKeyDown={handleChipKeyDown}
          >
            {t('allCategories')}
          </Link>
        </nav>
      ) : null}

      <BlogHubTags tags={tags} activeTag={activeTag} />
    </>
  );
};
